// VendorSignOffPage — the PUBLIC (no-login) page the payer's retrieval rep opens from the
// emailed sign-off link. The token in the URL is the authorization. It shows the patient
// manifest the rep collected + a short attestation form ("I, [name], collected these records
// on [date]") and POSTs the signature back. Single-use + expiry are enforced server-side.
// No PHI beyond the manifest the rep is already authorized to collect; nothing is persisted
// in the browser. Mobile-friendly (single column, big touch targets).
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

interface Ctx {
  recipient_name: string | null;
  requestor: string | null;
  business_associate: string | null;
  manifest: Array<{ patient_name: string | null; page_from: number | null; page_to: number | null }>;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-6 px-4">
      <div className="w-full max-w-xl">
        <div className="text-lg font-bold text-[#C94B1F] mb-3">RCMTask</div>
        {children}
      </div>
    </div>
  );
}

export default function VendorSignOffPage() {
  const { token } = useParams<{ token: string }>();
  const [ctx, setCtx] = useState<Ctx | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [form, setForm] = useState({ signer_name: '', signer_title: '', collected_date: '', signature_text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitErr, setSubmitErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/vendor-sign-off/${encodeURIComponent(token || '')}`);
        if (!r.ok) {
          setLoadErr(r.status === 410 ? 'This sign-off link has expired or was already used.' : 'This sign-off link is not valid.');
        } else {
          const j = await r.json();
          setCtx(j.data as Ctx);
        }
      } catch { setLoadErr('Could not load the sign-off page. Please try again.'); }
      setLoading(false);
    })();
  }, [token]);

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    setSubmitErr('');
    if (!form.signer_name.trim() || !form.signature_text.trim()) { setSubmitErr('Your name and signature are required.'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/vendor-sign-off/${encodeURIComponent(token || '')}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setSubmitErr((j as any).message || 'Could not submit your sign-off.'); }
      else setSubmitted(true);
    } catch { setSubmitErr('Could not submit your sign-off. Please try again.'); }
    setSubmitting(false);
  };

  if (loading) return <Shell><div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center text-sm text-gray-400">Loading…</div></Shell>;
  if (loadErr || !ctx) {
    return <Shell><div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center"><p className="text-sm text-gray-700">{loadErr || 'This link is not valid.'}</p></div></Shell>;
  }
  if (submitted) {
    return (
      <Shell>
        <div className="bg-white rounded-2xl shadow-sm border border-green-200 p-8 text-center">
          <div className="text-2xl mb-2">✓</div>
          <p className="text-base font-semibold text-green-800">Sign-off recorded</p>
          <p className="text-sm text-gray-600 mt-1">Thank you. Your acknowledgment has been recorded with the practice. You can close this page.</p>
        </div>
      </Shell>
    );
  }

  const inp = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5';
  return (
    <Shell>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h1 className="text-lg font-bold text-gray-900">Records retrieval — sign-off</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {ctx.requestor ? <>{ctx.requestor}{ctx.business_associate ? ` · via ${ctx.business_associate}` : ''}</> : 'Onsite records retrieval'}
        </p>
        <p className="text-xs text-gray-500 mt-2">
          Please confirm you retrieved the records for the patients below. This is a chain-of-custody acknowledgment for the practice's records.
        </p>

        <div className="mt-4">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Patients retrieved ({ctx.manifest.length})</p>
          {/* L7 — the names are shortened server-side for privacy on this public link. */}
          <p className="text-[11px] text-gray-400 mb-1">Names are shortened for privacy — match them against your printed worksheet.</p>
          <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-[11px] sticky top-0"><tr><th className="text-left px-3 py-1.5">Patient</th><th className="text-left px-3 py-1.5">Pages</th></tr></thead>
              <tbody>
                {ctx.manifest.map((m, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-3 py-1.5">{m.patient_name || '—'}</td>
                    <td className="px-3 py-1.5 text-xs">{m.page_from != null ? `${m.page_from}–${m.page_to ?? m.page_from}` : '—'}</td>
                  </tr>
                ))}
                {ctx.manifest.length === 0 && <tr><td colSpan={2} className="px-3 py-6 text-center text-gray-400 text-xs">No patients listed.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Your full name *</label>
            <input className={inp} value={form.signer_name} onChange={(e) => set('signer_name', e.target.value)} placeholder={ctx.recipient_name || 'Full name'} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title / company</label>
            <input className={inp} value={form.signer_title} onChange={(e) => set('signer_title', e.target.value)} placeholder="e.g. Retrieval Specialist, Datavant" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date retrieved</label>
            <input type="date" className={inp} value={form.collected_date} onChange={(e) => set('collected_date', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Signature (type your full name) *</label>
            <input className={inp} value={form.signature_text} onChange={(e) => set('signature_text', e.target.value)} placeholder="Type your name to sign" />
          </div>
          <p className="text-[11px] text-gray-500">By signing, you acknowledge you retrieved the records listed above on behalf of the requestor for the stated purpose.</p>
          {submitErr && <p className="text-xs text-red-600">{submitErr}</p>}
          <button onClick={submit} disabled={submitting} className="w-full py-3 rounded-xl bg-[#C94B1F] text-white text-sm font-semibold disabled:opacity-50">
            {submitting ? 'Submitting…' : 'Sign & submit'}
          </button>
        </div>
      </div>
    </Shell>
  );
}
